import {DeviceVisibilityEnum, LayerGroupTypeEnum} from "../../common/enum/enum";
import * as LZString from 'lz-string'
import {Style} from "../../common/config/Style";
import {
    CodeUniqueNameInfoConfig, RESOLUTIONS,
    verifyCoordinateLonLatWgs84,
    verifyExtentLonLatWgs84, WGS84_CODE
} from "../../common/config/config";
import {
    BaseLayerConfig,
    ContextLayerConfig,
    LayerConfig,
    LayerListElementConfig,
    LayerTreeElementConfig,
    MainConfig,
    Projection,
    RuntimeConfig,
    ZoomLevels
} from "../../common/config/MainConfig";
import {isBoolean} from "../../common/util";

/**
 * A runtime config for this map.
 * Tools and UI elements will change this runtime config, which will afterwards override MapConfig
 * @extends RuntimeConfig
 * @hideconstructor
 */
class MapRuntimeConfig extends RuntimeConfig {

    getClassName() {
        return "MapRuntimeConfig";
    }

    /**
     * Create a MapRuntimeConfig from a serialized object
     * @param code {string} Unique identifier for the runtime config
     * @param runtimeConfigSerialization {string} serialization
     * @return {MapRuntimeConfig}
     */
    static createFromSerialized(code, runtimeConfigSerialization) {
        let parsedConfig;
        try {
            parsedConfig = runtimeConfigSerialization
                // ? JSON.parse((runtimeConfigSerialization))
                ? JSON.parse(LZString.decompressFromBase64(runtimeConfigSerialization))
                : undefined;
            // tumbo.LOGGER.logDebug("Deserialized config: ", parsedConfig);
        } catch (e) {
            tumbo.LOGGER.logError('Error deserializing config', e);
        }
        return new MapRuntimeConfig(code, parsedConfig);
    }

    /**
     * Clean this runtime config
     */
    clean() {
        this.config = {
            m: {},
            bl: {},
            cl: {},
            l: {},
            c: {},
            t: {}
        };
    }

    /**
     * Set the current zoom level
     * @param zoomLevel {number} (integer)
     */
    setZoomLevel(zoomLevel) {
        this.config.m.z = zoomLevel;
    }

    /**
     * Get the current zoom level
     * @returns {number} (integer)
     */
    getZoomLevel() {
        return this.config.m.z;
    }

    /**
     * Set the current WGS84 center
     * @param centerLonLatWgs84 {Array<number>}
     */
    setCenterLonLatWgs84(centerLonLatWgs84) {
        if (centerLonLatWgs84[0] !== undefined && centerLonLatWgs84[1] !== undefined) {
            this.config.m.c = centerLonLatWgs84;
        }
    }

    /**
     * Get the current WGS84 center
     * @return {Array<number>}
     */
    getCenterLonLatWgs84() {
        return this.config.m.c;
    }

    /**
     * Set the current projection EPSG code
     * @param projection {string}
     */
    setProjection(projection) {
        this.config.m.p = projection;
    }

    /**
     * Get the current projection EPSG code
     * @return {string}
     */
    getProjection() {
        return this.config.m.p;
    }

    /**
     * set a classifier visibility
     * @param classifierCode {string}
     * @param visible {boolean}
     */
    setClassifierVisibility(classifierCode, visible) {
        if (!this.config.c) {
            this.config.c = {};
        }
        if (!this.config.c[classifierCode]) {
            this.config.c[classifierCode] = {};
        }
        this.config.c[classifierCode].v = visible;
    }

    /**
     * get a classifier visibility
     * @param classifierCode {string}
     * @return {boolean}
     */
    getClassifierVisibility(classifierCode) {
        return this.config.c && this.config.c[classifierCode]
            ? this.config.c[classifierCode].v
            : undefined;
    }

    /**
     * set a classifier expanded state
     * @param classifierCode {string}
     * @param expanded {boolean}
     */
    setClassifierExpanded(classifierCode, expanded) {
        if (!this.config.c) {
            this.config.c = {};
        }
        if (!this.config.c[classifierCode]) {
            this.config.c[classifierCode] = {};
        }
        this.config.c[classifierCode].e = expanded;
    }

    /**
     * get a classifier expanded state
     * @param classifierCode {string}
     * @return {boolean}
     */
    getClassifierExpanded(classifierCode) {
        return this.config.c && this.config.c[classifierCode]
            ? this.config.c[classifierCode].e
            : undefined;
    }

    /**
     * set a base layer visibility
     * @param baseLayerCode {string}
     * @param visible {boolean}
     */
    setBaseLayerVisibility(baseLayerCode, visible) {
        if (!this.config.bl) {
            this.config.bl = {};
        }
        if (!this.config.bl[baseLayerCode]) {
            this.config.bl[baseLayerCode] = {};
        }
        this.config.bl[baseLayerCode].v = visible;
    }

    /**
     * get a base layer visibility
     * @param baseLayerCode {string}
     * @return {boolean}
     */
    getBaseLayerVisibility(baseLayerCode) {
        return this.config.bl && this.config.bl[baseLayerCode]
            ? this.config.bl[baseLayerCode].v
            : undefined;
    }

    /**
     * set a context layer visibility
     * @param contextLayerCode {string}
     * @param visible {boolean}
     */
    setContextLayerVisibility(contextLayerCode, visible) {
        if (!this.config.cl) {
            this.config.cl = {};
        }
        if (!this.config.cl[contextLayerCode]) {
            this.config.cl[contextLayerCode] = {};
        }
        this.config.cl[contextLayerCode].v = visible;
    }

    /**
     * get a context layer visibility
     * @param contextLayerCode {string}
     * @return {boolean}
     */
    getContextLayerVisibility(contextLayerCode) {
        return this.config.cl && this.config.cl[contextLayerCode]
            ? this.config.cl[contextLayerCode].v
            : undefined;
    }

    /**
     * set a layer visibility
     * @param layerCode {string}
     * @param visible {boolean}
     */
    setLayerVisibility(layerCode, visible) {
        if (!this.config.l) {
            this.config.l = {};
        }
        if (!this.config.l[layerCode]) {
            this.config.l[layerCode] = {};
        }
        this.config.l[layerCode].v = visible;
    }

    /**
     * get a layer visibility
     * @param layerCode {string}
     * @return {boolean}
     */
    getLayerVisibility(layerCode) {
        return this.config.l && this.config.l[layerCode]
            ? this.config.l[layerCode].v
            : undefined;
    }

    /**
     * set a layer opacity
     * @param layerCode {string}
     * @param opacity {number} [0 .. 1]
     */
    setLayerOpacity(layerCode, opacity) {
        if (!this.config.l) {
            this.config.l = {};
        }
        if (!this.config.l[layerCode]) {
            this.config.l[layerCode] = {};
        }
        this.config.l[layerCode].o = opacity;
    }

    /**
     * get a layer opacity
     * @param layerCode {string}
     * @return {number} [0 .. 1]
     */
    getLayerOpacity(layerCode) {
        return this.config.l && this.config.l[layerCode]
            ? this.config.l[layerCode].o
            : undefined;
    }

    /**
     * set a layer time code
     * @param layerCode {string}
     * @param timeCode {string}
     */
    setLayerTimeCode(layerCode, timeCode) {
        if (!this.config.l) {
            this.config.l = {};
        }
        if (!this.config.l[layerCode]) {
            this.config.l[layerCode] = {};
        }
        this.config.l[layerCode].tc = {"tc": timeCode, "ts": new Date()};
    }

    /**
     * get a layer time code
     * @param layerCode {string}
     * @return {string}
     */
    getLayerTimeCode(layerCode) {
        return this.config.l && this.config.l[layerCode]
            ? this.config.l[layerCode].tc
            : undefined;
    }

    /**
     * set a layer time scale
     * @param layerCode {string}
     * @param timeScale {string}
     */
    setLayerTimeScale(layerCode, timeScale) {
        if (!this.config.l) {
            this.config.l = {};
        }
        if (!this.config.l[layerCode]) {
            this.config.l[layerCode] = {};
        }
        this.config.l[layerCode].ts = timeScale;
    }

    /**
     * get a layer time scale
     * @param layerCode {string}
     * @return {string}
     */
    getLayerTimeScale(layerCode) {
        return this.config.l && this.config.l[layerCode]
            ? this.config.l[layerCode].ts
            : undefined;
    }
}


/**
 * The configuration of the map
 * @extends MainConfig
 * @hideconstructor
 */
class MapConfig extends MainConfig {

    getClassName() {
        return "MapConfig";
    }

    getName() {
        return this.config.map.name;
    }

    getCode() {
        return this.config.map.code;
    }

    /**
     * Current map zoom level
     * @return {number} (integer)
     */
    getZoomLevel() {
        return this.config.map.zoomLevel;
    }

    /**
     * Current map center in WGS84
     * @return {Array<number>}
     */
    getCenterLonLatWgs84() {
        return this.config.map.centerLonLatWgs84;
    }

    _getStatsPostUrlProperty() {
        return this.config.map.statsPostUrl;
    }

    _getContactPostUrlProperty() {
        return this.config.map.contactPostUrl;
    }

    /**
     * Maximum map extent in WGS84
     * @return {Array<number>}
     */
    getMaxExtentWgs84() {
        return this.config.map.maxExtent
            ? this.config.map.maxExtent
            : [-180, -90, 180, 90];
    }

    /**
     * List of available projections
     * @return {Array<Projection>}
     */
    getProjectionConfigs() {
        let projs = [];
        if (this.config.projections) {
            for (let p in this.config.projections) {
                projs.push(new Projection(this.config.projections[p]))
            }
        }
        return projs;
    }

    /**
     * Memory cached version of the list of available projections
     * @return {Array<Projection>}
     */
    getCachedProjectionConfigs() {
        if (!this.cachedProjectionConfigs) {
            this.cachedProjectionConfigs = this.getProjectionConfigs();
        }
        return this.cachedProjectionConfigs;
    }

    /**
     * Memory cached version of a projection given its EPSG code
     * @param code {string}
     * @return {Projection}
     */
    getCachedProjectionConfig(code) {
        if (!this.cachedProjectionConfigsMap) {
            this.cachedProjectionConfigsMap = {};
            for (let cpc in this.getCachedProjectionConfigs()) {
                const cachedProjectionConfig = this.getCachedProjectionConfigs()[cpc];
                this.cachedProjectionConfigsMap[cachedProjectionConfig.getCode()] = cachedProjectionConfig;
            }
        }
        return this.cachedProjectionConfigsMap[code];
    }

    /**
     * All the configs for the layer tree
     * @param includeDisabled {boolean} if true, includes disabled elements
     * @return {Array<LayerTreeElementConfig>}
     */
    getLayerConfigTree(includeDisabled = false) {
        const layerConfigTree = [];
        for (const lt in this.config.map.layerTree) {
            const conf = this.config.map.layerTree[lt];
            if (conf.type === LayerGroupTypeEnum.CLASSIFIER.getCode()) {
                const layerTreeClassifierConfig = new LayerTreeClassifierConfig(conf, this.allLayerServices, this.allProducts, this.allStyles);
                if (includeDisabled || !layerTreeClassifierConfig.isDisabled())
                    layerConfigTree.push(layerTreeClassifierConfig);
            } else if (conf.type === LayerGroupTypeEnum.LAYER.getCode()) {
                const layerConfig = new LayerConfig(conf, this.allLayerServices, this.allProducts, this.allStyles);
                if (includeDisabled || !layerConfig.isDisabled())
                    layerConfigTree.push(layerConfig);
            }
        }
        return layerConfigTree;
    }

    /**
     * This map's base layers configs
     * @param includeDisabled {boolean} if true, includes disabled elements
     * @return {Array<LayerListElementConfig>}
     */
    getBaseLayerConfigList(includeDisabled = false) {
        const baseLayerConfigList = [];
        for (const lt in this.config.map.baseLayers) {
            const conf = this.config.map.baseLayers[lt];
            if (conf.type === LayerGroupTypeEnum.CLASSIFIER.getCode()) {
                const layerListClassifierConfig = new LayerListClassifierConfig("BaseLayerConfig", conf, this.allLayerServices, this.allProducts, this.allStyles);
                if (includeDisabled || !layerListClassifierConfig.isDisabled())
                    baseLayerConfigList.push(layerListClassifierConfig);
            } else {
                const baseLayerConfig = new BaseLayerConfig(conf, this.allLayerServices[conf.srvCode], this.allProducts[conf.prodCode], this.allStyles);
                if (includeDisabled || !baseLayerConfig.isDisabled())
                    baseLayerConfigList.push(baseLayerConfig);
            }
        }
        return baseLayerConfigList;
    }

    /**
     * This map's context layers configs
     * @param includeDisabled {boolean} if true, includes disabled elements
     * @return {Array<LayerListElementConfig>}
     */
    getContextLayerConfigList(includeDisabled = false) {
        const contextLayerConfigList = [];
        for (const lt in this.config.map.contextLayers) {
            const conf = this.config.map.contextLayers[lt];
            if (conf.type === LayerGroupTypeEnum.CLASSIFIER.getCode()) {
                const layerListClassifierConfig = new LayerListClassifierConfig("ContextLayerConfig", conf, this.allLayerServices, this.allProducts, this.allStyles);
                if (includeDisabled || !layerListClassifierConfig.isDisabled())
                    contextLayerConfigList.push(layerListClassifierConfig);
            } else {
                const contextLayerConfig = new ContextLayerConfig(conf, this.allLayerServices[conf.srvCode], this.allProducts[conf.prodCode], this.allStyles);
                if (includeDisabled || !contextLayerConfig.isDisabled())
                    contextLayerConfigList.push(contextLayerConfig);
            }
        }
        return contextLayerConfigList;
    }

    /**
     * All tool configs
     * @return {Array<ToolConfig>}
     */
    getToolConfigs() {
        if (!this.tools) {
            this.tools = [];
            for (let tg in this.config.tools) {
                this.tools.push(new ToolConfig(this.config.tools[tg]));
            }
        }
        return this.tools;
    }

    /**
     * Zoom levels
     * @return {ZoomLevels|undefined}
     */
    getZoomLevels() {
        return this.config.map.zoomLevels ? new ZoomLevels(this.config.map.zoomLevels) : undefined;
    }

    /**
     *
     * @return {Projection}
     */
    getProjection() {
        const projectionCode = this.config.map.projection && this.config.map.projection.trim().length > 0
            ? this.config.map.projection.trim()
            : WGS84_CODE;
        return this.getCachedProjectionConfigs().filter(p => p.getCode() === projectionCode)[0];
    }

    /**
     * Override this config
     * @param runtimeConfig {MapRuntimeConfig} The configuration override
     */
    override(runtimeConfig) {
        if (runtimeConfig.getZoomLevel() !== undefined) {
            this.config.map.zoomLevel = runtimeConfig.getZoomLevel();
        }
        if (runtimeConfig.getCenterLonLatWgs84() !== undefined) {
            this.config.map.centerLonLatWgs84 = runtimeConfig.getCenterLonLatWgs84();
        }

        this.getBaseLayerConfigList().forEach(element => {
            element.override(runtimeConfig);
            if (element instanceof LayerListClassifierConfig) {
                element.getChildren().forEach(e => e.override(runtimeConfig));
            }
        });

        this.getContextLayerConfigList().forEach(element => {
            element.override(runtimeConfig);
            if (element instanceof LayerListClassifierConfig) {
                element.getChildren().forEach(e => e.override(runtimeConfig));
            }
        });

        function overrideTreeElements(layerTreeElementConfigs) {
            if (layerTreeElementConfigs.length === 0) {
                return;
            }
            for (let lte in layerTreeElementConfigs) {
                let layerTreeElementConfig = layerTreeElementConfigs[lte];
                layerTreeElementConfig.override(runtimeConfig);
                overrideTreeElements(layerTreeElementConfig.getChildren());
            }
        }

        overrideTreeElements(this.getLayerConfigTree());
    };

    /**
     * Verify this configuration and return a list of errors
     * @return {Array<string>}
     */
    verify() {
        let errors = super.verify();

        if (!this.getMaxExtentWgs84()) {
            errors.push('MapConfig: maxExtentLonLatWgs84 is mandatory');
        } else {
            if (!verifyExtentLonLatWgs84(this.getMaxExtentWgs84())) {
                errors.push('MapConfig: invalid maxExtentLonLatWgs84. Should be [minLon, minLat, maxLon, maxLat]');
            }
        }

        if (this.getCenterLonLatWgs84()) {
            if (!verifyCoordinateLonLatWgs84(this.getCenterLonLatWgs84())) {
                errors.push('MapConfig: invalid centerLonLatWgs84. Should be [lon, lat]');
            }
        }

        const projectionConfigs = this.getProjectionConfigs();
        if (projectionConfigs.length === 0) {
            errors.push('MapConfig: projections is mandatory');
        } else {
            for (let p in projectionConfigs) {
                errors = errors.concat(projectionConfigs[p].verify());
            }
        }

        if (Object.keys(this.allProducts).length === 0)
            errors.push('MapConfig: products is mandatory');
        else {
            for (let p in this.allProducts) {
                errors = errors.concat(this.allProducts[p].verify());
            }
        }

        if (Object.keys(this.allLayerServices).length === 0)
            errors.push('MapConfig: services is mandatory');
        else {
            for (let p in this.allLayerServices) {
                errors = errors.concat(this.allLayerServices[p].verify());
            }
        }

        if (Object.keys(this.allStyles).length === 0)
            errors.push('MapConfig: styles is mandatory');
        else {
            for (let p in this.allStyles) {
                errors = errors.concat(this.allStyles[p].verify());
            }
        }

        const zoomLevels = this.getZoomLevels();
        if (!zoomLevels) {
            errors.push('MapConfig: zoomLevels is mandatory');
        } else {
            errors = errors.concat(zoomLevels.verify());
            const levels = zoomLevels.getLevels();
            if (!levels || levels < 1 || levels > RESOLUTIONS.length)
                errors.push('MapConfig: levels must be a number between 1 and ' + RESOLUTIONS.length);

            const zoomLevel = this.getZoomLevel();
            if (zoomLevel !== undefined && (zoomLevel < 0 || zoomLevel > zoomLevels.getMaxResolutionIndex())) {
                errors.push('MapConfig: invalid zoomLevel');
            }
        }

        const baseLayerListElements = this.getBaseLayerConfigList(true);
        let baseLayersVisibleCount = 0;
        if (!baseLayerListElements || baseLayerListElements.filter(bl=>!bl.isDisabled()).length === 0) {
            errors.push('MapConfig: baseLayers is mandatory');
        } else {
            for (let ble in baseLayerListElements) {
                errors = errors.concat(baseLayerListElements[ble].verify());
                if(!baseLayerListElements[ble].isDisabled()){
                if (baseLayerListElements[ble] instanceof BaseLayerConfig) {
                    if (baseLayerListElements[ble].isVisible()) {
                        baseLayersVisibleCount++;
                    }
                } else {
                    const baseLayerConfigs = baseLayerListElements[ble].getChildren();
                    for (const bl in baseLayerConfigs) {
                        if (baseLayerConfigs[bl].isVisible()) {
                            baseLayersVisibleCount++;
                        }
                    }
                }
                }
            }
            if (baseLayersVisibleCount !== 1) {
                errors = errors.concat("MapConfig: One and only one base layer must be visible");
            }
        }

        const contextLayerListElements = this.getContextLayerConfigList(true);
        for (const cle in contextLayerListElements) {
            errors = errors.concat(contextLayerListElements[cle].verify());
        }

        let layerTree = this.getLayerConfigTree(true);
        if (!layerTree || layerTree.filter(bl=>!bl.isDisabled()).length === 0) {
            errors.push('MapConfig: layerTree is mandatory');
        } else {
            for (let lte in layerTree) {
                errors = errors.concat(layerTree[lte].verify());
            }
        }

        let toolConfigs = this.getToolConfigs();
        if (!toolConfigs || toolConfigs.length === 0) {
            errors.push('MapConfig: tools is mandatory');
        } else {
            for (let tg in toolConfigs) {
                errors = errors.concat(toolConfigs[tg].verify());
            }
        }

        return errors;
    }
}


/**
 * The config of layer tree classifier, which is a tree node
 * @extends LayerTreeElementConfig
 */
class LayerTreeClassifierConfig extends LayerTreeElementConfig {

    /**
     *
     * @param config {Object} This classifier config
     * @param allLayerServices {Object<string,LayerServiceConfig>} All the available layer services by code
     * @param allProducts {Object<String,ProductConfig>} All the available products
     * @param allStyles {Object<String,Style>} All the available styles
     */
    constructor(config, allLayerServices, allProducts, allStyles) {
        super(config);
        this.allLayerServices = allLayerServices;
        this.allProducts = allProducts;
        this.allStyles = allStyles;
    }

    getClassName() {
        return "LayerTreeClassifierConfig";
    }

    /**
     * The name of this classifier
     * @return {string}
     */
    getName() {
        return this.config.name;
    }

    /**
     * Is this classifier expanded?
     * @return {boolean}
     */
    isExpanded() {
        return (this.config.expanded === true);
    }

    /**
     * Is this classifier mutually exclusive.
     * If true, one and only one of its children must be visible
     * @return {boolean}
     */
    isMutuallyExclusive() {
        return this.config.mutuallyExclusive;
    }

    getChildren(includeDisabled = false) {
        let children = [];
        for (let c in this.config.children) {
            const conf = this.config.children[c];
            if (conf.type === LayerGroupTypeEnum.CLASSIFIER.getCode()) {
                const classifier = new LayerTreeClassifierConfig(conf, this.allLayerServices, this.allProducts, this.allStyles);
                if (includeDisabled || !classifier.isDisabled())
                    children.push(classifier);
            } else if (conf.type === LayerGroupTypeEnum.LAYER.getCode()) {
                const layer = new LayerConfig(conf, this.allLayerServices, this.allProducts, this.allStyles);
                if (includeDisabled || !layer.isDisabled())
                    children.push(layer);
            }
        }
        return children;
    }

    override(runtimeConfig) {
        super.override(runtimeConfig);
        if (runtimeConfig.getClassifierExpanded(this.getCode()) !== undefined) {
            this.config.expanded = runtimeConfig.getClassifierExpanded(this.getCode());
        }
        if (runtimeConfig.getClassifierVisibility(this.getCode()) !== undefined) {
            this.config.visible = runtimeConfig.getClassifierVisibility(this.getCode());
        }
    }

    verify() {
        let errors = super.verify(this);

        if (!this.getName() || this.getName().length === 0) {
            errors.push(this.getLogId() + ": name is mandatory");
        }

        let children = this.getChildren(true);
        let visibleChildrenCount = 0;
        const ordinalCounts = {};
        for (let c in children) {
            errors = errors.concat(children[c].verify());
            if (!children[c].isDisabled()) {
                if (children[c].isVisible()) {
                    visibleChildrenCount++;
                }
                if (children[c].getRenderOrdinal() !== null && children[c].getRenderOrdinal() !== undefined) {
                    ordinalCounts[children[c].getRenderOrdinal()] = ordinalCounts[children[c].getRenderOrdinal()] ? ordinalCounts[children[c].getRenderOrdinal()] + 1 : 1;
                }
            }
        }
        if (this.isMutuallyExclusive() && visibleChildrenCount !== 1) {
            errors.push(this.getLogId() + ": one and only one child must be visible");
        }
        const ordinalKeys = Object.keys(ordinalCounts);
        if (ordinalKeys.length > 0 && ordinalKeys.length !== children.length) {
            errors.push(this.getLogId() + ": If an ordinal is defined for a child of a classifier, every sibling must have a unique ordinal");
        }

        return errors;
    }
}

/**
 * The config of layer list classifier, which is a tree node
 * @extends LayerListElementConfig
 */
class LayerListClassifierConfig extends LayerListElementConfig {
    /**
     * @param layerClass {string} Class name of the layers of this classifier (base or context?)
     * @param config {Object} This classifier config
     * @param allLayerServices {Object<string,LayerServiceConfig>} All the available layer services by code
     * @param allProducts {Object<String,ProductConfig>} All the available products
     * @param allStyles {Object<String,Style>} All the available styles
     */
    constructor(layerClass, config, allLayerServices, allProducts, allStyles) {
        super(config);
        this.allLayerServices = allLayerServices;
        this.allProducts = allProducts;
        this.allStyles = allStyles;
        this.layerClass = layerClass;
    }

    getClassName() {
        return "LayerListClassifierConfig";
    }

    /**
     * The name of this classifier
     * @return {string}
     */
    getName() {
        return this.config.name;
    }

    /**
     * Is this classifier expanded?
     * @return {boolean}
     */
    isExpanded() {
        return (this.config.expanded === true);
    }

    getChildren(includeDisabled = false) {
        let children = [];
        for (let c in this.config.children) {
            const conf = this.config.children[c];
            if (this.layerClass === 'BaseLayerConfig') {
                const baseLayerConfig = new BaseLayerConfig(conf, this.allLayerServices[conf.srvCode], this.allProducts[conf.prodCode], this.allStyles);
                if (includeDisabled || !baseLayerConfig.isDisabled())
                    children.push(baseLayerConfig);
            } else if (this.layerClass === 'ContextLayerConfig') {
                const contextLayerConfig = new ContextLayerConfig(conf, this.allLayerServices[conf.srvCode], this.allProducts[conf.prodCode], this.allStyles);
                if (includeDisabled || !contextLayerConfig.isDisabled())
                    children.push(contextLayerConfig);
            }
        }
        return children;
    }

    override(runtimeConfig) {
        super.override(runtimeConfig);
        if (runtimeConfig.getClassifierExpanded(this.getCode()) !== undefined) {
            this.config.expanded = runtimeConfig.getClassifierExpanded(this.getCode());
        }
    }

    verify() {
        let errors = super.verify(this);

        if (!this.getName() || this.getName().length === 0) {
            errors.push(this.getLogId() + ": name is mandatory");
        }

        let children = this.getChildren();
        for (let c in children) {
            errors = errors.concat(children[c].verify());
        }

        return errors;
    }
}

/**
 * A tool configuration
 * @extends CodeUniqueNameInfoConfig
 * @hideconstructor
 */
class ToolConfig extends CodeUniqueNameInfoConfig {
    getClassName() {
        return "ToolConfig";
    }

    /**
     * Device visibility of this tool
     * @return {DeviceVisibilityEnum}
     */
    getDeviceVisibility() {
        return DeviceVisibilityEnum.getForCode(this.config.deviceVisibility);
    }

    verify() {
        let errors = super.verify();
        if (this.config.deviceVisibility && this.config.deviceVisibility.length > 0
            && !DeviceVisibilityEnum.exists(this.config.deviceVisibility)) {
            errors.push(this.getLogId() + ": invalid deviceVisibility. Possible values: " + DeviceVisibilityEnum.getPossibleCodes());
        }
        return errors;
    }

    /**
     * A map of other properties, specific for certain tools.
     * Each tool implementation should know what to look for in here, if anything at all
     * @return {Object<string,*>}
     */
    getOtherProperties() {
        let otherProperties = {};
        Object.keys(this.config).forEach((key, index) => {
            if (key !== 'code' && key !== 'nameKey' && key !== 'infoKey') {
                otherProperties[key] = this.config[key];
            }
        });
        return otherProperties;
    }
}


export {MapConfig, MapRuntimeConfig, ToolConfig}